/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable, Injector } from '@angular/core';
import { ViewService } from '@angular-kit/view';
import { ContainerModel, ContextBindings, FormFieldModel, TextInputModel, ViewComponentKey } from '@typescript-kit/view';
import { SharedViewKey } from '../key/shared-view.key';
import { LetterHeaderViewData, LetterHeaderViewModel } from './letter-header.view-model';
import { LetterHeaderKey } from '@teamworks/global';
import { ChangedEvent } from '@typescript-kit/core';
import { ShellViewService } from '../../shell/view/shell.view-service';

@Injectable()
export class LetterHeaderViewService extends ViewService<LetterHeaderViewModel, ContainerModel> {
  private readonly shellViewService: ShellViewService;

  constructor(injector: Injector) {
    super(injector);
    this.shellViewService = injector.get(ShellViewService);
  }

  initialize(viewData?: LetterHeaderViewData) {
    const viewModel = viewData instanceof LetterHeaderViewModel ? viewData : new LetterHeaderViewModel(viewData);
    super.initialize(viewModel);
  }

  protected onViewModelPropertyChanged(event: ChangedEvent) {
    super.onViewModelPropertyChanged(event);
  }

  protected createComponentModel(): ContainerModel {
    return new ContainerModel({
      tags: ['app-letter-header'],
      items: [
        this.createInputFieldModel(LetterHeaderKey.SENDER, ViewComponentKey.TEXT_INPUT),
        this.createInputFieldModel(LetterHeaderKey.RECIPIENT, ViewComponentKey.TEXT_AREA),
        this.createInputFieldModel(LetterHeaderKey.EDITOR, ViewComponentKey.TEXT_AREA),
        this.createInputFieldModel(LetterHeaderKey.SUBJECT, ViewComponentKey.TEXT_INPUT),
        this.createInputFieldModel(LetterHeaderKey.SALUTATION, ViewComponentKey.TEXT_AREA)
      ]
    });
  }

  protected refreshComponentModel(model: ContainerModel) {
    this.refreshInputFieldModel(model.items.get(`${LetterHeaderKey.SENDER}Field`) as FormFieldModel);
    this.refreshInputFieldModel(model.items.get(`${LetterHeaderKey.RECIPIENT}Field`) as FormFieldModel);
    this.refreshInputFieldModel(model.items.get(`${LetterHeaderKey.EDITOR}Field`) as FormFieldModel);
    this.refreshInputFieldModel(model.items.get(`${LetterHeaderKey.SUBJECT}Field`) as FormFieldModel);
    this.refreshInputFieldModel(model.items.get(`${LetterHeaderKey.SALUTATION}Field`) as FormFieldModel);
  }

  private createInputFieldModel(key: string, type: string): FormFieldModel {
    if (this.shellViewService.viewModel.isPrinting) {
      type = ViewComponentKey.VALUE;
    }
    const model = new FormFieldModel({
      key: `${key}Field`,
      tags: [`app-letter-header-${key}`],
      label: true,
      control: new TextInputModel({
        key, scope: SharedViewKey.LETTER_HEADER,
        type, tags: ['form-control', 'form-control-sm', `app-letter-header-${key}-control`],
        autoselect: type === ViewComponentKey.TEXT_INPUT
      })
    });
    this.refreshInputFieldModel(model);
    return model;
  }

  private refreshInputFieldModel(model: FormFieldModel) {
    const context = this.viewModel.letterHeader;
    model.control.setValues({
      context,
      contextBinding: context ? ContextBindings.TWO_WAY : ContextBindings.NONE
    });
  }

}
